// src/components/Sidebar.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import user from "../assets/image/user.jpg";
import patient from "../assets/image/patient.png";

import {
  FaTachometerAlt,
  FaCalendarAlt,
  FaNotesMedical,
  FaUserAlt,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaClipboardCheck,
  FaChevronDown,
  FaTooth,
  FaUserMd,
  FaUsers,
  FaFileAlt,
  FaStethoscope,
} from "react-icons/fa";

import logo from "./../assets/logo-1.png";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const dropdownRef = useRef(null);
  const sidebarRef = useRef(null);

  const {
    loginUser,
    logout,
    currentUserProfile,
    currentUserProfileLoading,
    getUserDisplayName,
    getUserEmail,
  } = useContext(AuthContext);

  const location = useLocation();

  // Initialize mounted state for smooth animations
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Handle click outside for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      // Also close sidebar when clicking outside on mobile/tablet
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        isOpen &&
        window.innerWidth < 1024
      ) {
        const isMenuButton = event.target.closest('[data-menu-button]');
        if (!isMenuButton) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
        setDropdownOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Get user display name and email
  const displayName = getUserDisplayName();
  const userEmail = getUserEmail();

  // Get profile image based on role
  const getProfileImage = () => {
    switch (loginUser?.role) {
      case "patient":
        return patient;
      case "doctor":
      case "receptionist":
      case "admin":
      default:
        return user;
    }
  };

  // Get role display name with better formatting
  const getRoleDisplayName = () => {
    switch (loginUser?.role) {
      case "patient":
        return "Patient";
      case "doctor":
        return "Doctor";
      case "receptionist":
        return "Receptionist";
      case "admin":
        return "Administrator";
      default:
        return loginUser?.role || "User";
    }
  };

  // Get role badge color
  const getRoleBadgeColor = () => {
    switch (loginUser?.role) {
      case "patient":
        return "bg-blue-500/20 text-blue-400";
      case "doctor":
        return "bg-green-500/20 text-green-400";
      case "receptionist":
        return "bg-purple-500/20 text-purple-400";
      case "admin":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  // Navigation with dental clinic specific icons
  const navigation = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: <FaTachometerAlt />,
      roles: ["patient", "receptionist", "doctor", "admin"],
      description: "Clinic Dashboard",
    },
    {
      name: "Create OPD Form",
      path: "/dashboard/opd-form",
      icon: <FaStethoscope />,
      roles: ["receptionist", "doctor"],
      description: "New OPD Entry",
    },
    {
      name: "OPD Records",
      path: "/dashboard/opd-list",
      icon: <FaClipboardCheck />,
      roles: ["receptionist", "doctor", "admin"],
      description: "OPD History",
    },
    {
      name: "All Patients",
      path: "/dashboard/user-list",
      icon: <FaUsers />,
      roles: ["receptionist", "doctor", "admin"],
      description: "Manage Patients",
    },
    {
      name: "All Users",
      path: "/dashboard/all-users",
      icon: <FaUsers />,
      roles: ["admin"],
      description: "User Management",
    },
    {
      name: "Monthly Analytics",
      path: "/dashboard/monthly-analytics",
      icon: <FaClipboardCheck />,
      roles: ["admin"],
      description: "Monthly Reports",
    },
    {
      name: "Expense Manager",
      path: "/dashboard/expense-manager",
      icon: <FaFileAlt />,
      roles: ["admin"],
      description: "Manage Expenses",
    },
    {
      name: "Appointments",
      path: "/dashboard/appointment",
      icon: <FaCalendarAlt />,
      roles: ["patient", "receptionist", "doctor"],
      description: "Book & View Appointments",
    },
    {
      name: "All Visits",
      path: "/dashboard/visits",
      icon: <FaNotesMedical />,
      roles: ["receptionist", "doctor"],
      description: "Visit History",
    },
    {
      name: "Medical Records",
      path: "/dashboard/medical-details",
      icon: <FaFileAlt />,
      roles: ["patient"],
      description: "Personal Medical Records",
    },
    
    
    
    {
      name: "Dropdown Manager",
      path: "/dashboard/dropdown-manager",
      icon: <FaChevronDown />,
      roles: ["doctor", "receptionist", "admin"],
      description: "Dropdown Settings",
    },
    {
      name: "My Profile",
      path: "/dashboard/profile",
      icon: <FaUserAlt />,
      roles: ["patient", "receptionist"],
      description: "Profile Settings",
    },
  ];

  const filteredNavigation = navigation.filter((item) =>
    item.roles.includes(loginUser?.role)
  );

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile/Tablet Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-lg z-30">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
          {/* Left Section: Logo & Brand */}
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Mahalaxmi Dental Clinic"
              className="h-8 sm:h-10 object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="text-sm font-semibold text-white">Mahalaxmi</h1>
              <p className="text-xs text-gray-300">Dental Clinic</p>
            </div>
          </div>

          {/* Right Section: User Info & Menu */}
          <div className="flex items-center gap-3">
            {/* User Avatar - Visible on mobile */}
            <button
              onClick={toggleDropdown}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition-all duration-200"
            >
              <img
                src={getProfileImage()}
                className="h-8 w-8 rounded-full ring-2 ring-white/20"
                alt="User Avatar"
              />
              <FaChevronDown
                className={`hidden sm:block w-3 h-3 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {/* Menu Toggle Button */}
            <button
              onClick={toggleSidebar}
              data-menu-button
              aria-label="Toggle Menu"
              className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200 active:scale-95"
            >
              <div className="relative w-6 h-6">
                <FaBars
                  className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'
                    }`}
                />
                <FaTimes
                  className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'
                    }`}
                />
              </div>
            </button>
          </div>

          {/* User Dropdown Menu */}
          <div
            ref={dropdownRef}
            className={`absolute right-4 top-full mt-2 bg-white text-gray-800 shadow-xl rounded-xl overflow-hidden transition-all duration-200 ${dropdownOpen
                ? 'opacity-100 translate-y-0 visible'
                : 'opacity-0 -translate-y-2 invisible'
              }`}
            style={{ minWidth: '240px' }}
          >
            {/* User Info Header */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-50 p-4">
              <div className="flex items-center gap-3">
                <img
                  src={getProfileImage()}
                  className="h-12 w-12 rounded-full ring-2 ring-white shadow-md"
                  alt="User Avatar"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {currentUserProfileLoading ? "Loading..." : displayName}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{userEmail}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor()}`}>
                    {getRoleDisplayName()}
                  </span>
                </div>
              </div>
            </div>

            {/* Dropdown Actions */}
            <div className="py-2">
              {loginUser?.role !== "doctor" && (
                <Link
                  to="/dashboard/profile"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 transition-colors"
                >
                  <FaUserAlt className="text-gray-500" />
                  <span className="text-gray-700">View Profile</span>
                </Link>
              )}
              <button
                onClick={() => {
                  handleLogout();
                  setDropdownOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-2.5 w-full text-left hover:bg-red-50 text-red-600 transition-colors"
              >
                <FaSignOutAlt />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header on mobile/tablet */}
      <div className="lg:hidden h-14 sm:h-16" />

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full w-72 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800 text-white flex flex-col shadow-2xl transform transition-transform duration-300 ease-out z-50 ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 lg:static lg:shadow-xl`}
      >
        {/* Logo Section */}
        <div className="p-6 bg-gradient-to-r from-blue-600/10 to-transparent">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
              <img
                src="/logo.png"
                alt="Mahalaxmi Dental Clinic"
                className="h-10 w-10 object-contain"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Mahalaxmi</h2>
              <p className="text-xs text-gray-400">Dental Clinic & Implant Center</p>
            </div>
          </Link>
        </div>

        {/* User Profile Card */}
        <div className="mx-4 mb-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={getProfileImage()}
                className="h-14 w-14 rounded-full ring-2 ring-white/20 shadow-lg"
                alt="User Avatar"
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900"></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate">
                {currentUserProfileLoading ? (
                  <span className="inline-block h-4 w-24 bg-white/20 rounded animate-pulse"></span>
                ) : (
                  displayName
                )}
              </p>
              <p className="text-xs text-gray-400 truncate">{userEmail}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor()}`}>
                {getRoleDisplayName()}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 pb-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <ul className="space-y-1">
            {filteredNavigation.map((item, index) => (
              <li
                key={item.name}
                style={{
                  animation: isMounted ? `slideIn 0.3s ease-out ${index * 0.05}s both` : 'none'
                }}
              >
                <Link
                  to={item.path}
                  className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive(item.path)
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  onClick={() => setIsOpen(false)}
                >
                  <span className={`text-xl transition-transform duration-200 ${!isActive(item.path) && 'group-hover:scale-110'
                    }`}>
                    {item.icon}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-xs opacity-75">{item.description}</p>
                  </div>
                  {isActive(item.path) && (
                    <div className="w-1 h-6 bg-white/30 rounded-full"></div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-white/10 bg-gradient-to-t from-gray-900 to-transparent">
          <button
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-red-600/20 to-red-500/20 hover:from-red-600 hover:to-red-500 text-white rounded-xl transition-all duration-200 group"
          >
            <FaSignOutAlt className="group-hover:rotate-12 transition-transform duration-200" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Add keyframe animations */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .scrollbar-thin {
          scrollbar-width: thin;
        }

        .scrollbar-thumb-gray-700::-webkit-scrollbar-thumb {
          background-color: #374151;
          border-radius: 8px;
        }

        .scrollbar-track-transparent::-webkit-scrollbar-track {
          background-color: transparent;
        }

        ::-webkit-scrollbar {
          width: 6px;
        }
      `}</style>
    </>
  );
};

export default Sidebar;
